/**
 * SearchContext - Bridges the header search bar and per-view filtering
 *
 * The unified search bar in the header writes searchQuery here.
 * Each view reads searchQuery to filter its local data in real-time.
 * Cross-entity results are shown in a dropdown by UnifiedSearchBar.
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

// Per-view search configuration
const VIEW_SEARCH_CONFIG = {
  vehicles: {
    entityType: 'vehicle',
    label: 'Véhicules',
    placeholder: 'Rechercher dans Véhicules — immatriculation, marque, modèle, VIN...'
  },
  drivers: {
    entityType: 'driver',
    label: 'Conducteurs',
    placeholder: 'Rechercher dans Conducteurs — nom, email, téléphone, n° permis...'
  },
  maintenance: {
    entityType: 'maintenance',
    label: 'Maintenance',
    placeholder: 'Rechercher dans Maintenance — titre, véhicule, type, statut...'
  },
  claims: {
    entityType: 'claim',
    label: 'Sinistres',
    placeholder: 'Rechercher dans Sinistres — numéro, véhicule, conducteur, lieu...'
  },
  fines: {
    entityType: 'fine',
    label: 'Amendes',
    placeholder: 'Rechercher dans Amendes — référence, véhicule, conducteur...'
  },
  documents: {
    entityType: 'document',
    label: 'Documents',
    placeholder: 'Rechercher dans Documents — nom, référence, type...'
  },
  sites: {
    entityType: 'site',
    label: 'Sites',
    placeholder: 'Rechercher dans Sites — nom, ville, adresse...'
  },
  fuel: {
    entityType: 'fuel',
    label: 'Carburant',
    placeholder: 'Rechercher dans Carburant — véhicule, date, type, station...'
  },
  inspections: {
    entityType: 'inspection',
    label: 'Inspections',
    placeholder: 'Rechercher dans Inspections — véhicule, date, type, conducteur...'
  },
  insurance: {
    entityType: 'insurance',
    label: 'Assurances',
    placeholder: 'Rechercher dans Assurances — contrat, véhicule, assureur...'
  },
  teams: {
    entityType: 'team',
    label: 'Équipes',
    placeholder: 'Rechercher dans Équipes — nom, code, description...'
  }
};

const DEFAULT_CONFIG = {
  entityType: null,
  label: '',
  placeholder: 'Rechercher...'
};

const SearchContext = createContext(null);

export function SearchProvider({ children, currentView }) {
  const [searchQuery, setSearchQuery] = useState('');

  const viewConfig = useMemo(
    () => VIEW_SEARCH_CONFIG[currentView] || DEFAULT_CONFIG,
    [currentView]
  );

  // Clear search when view changes
  useEffect(() => {
    setSearchQuery('');
  }, [currentView]);

  const clearSearch = () => setSearchQuery('');

  const value = useMemo(() => ({
    searchQuery,
    setSearchQuery,
    clearSearch,
    currentView,
    viewConfig
  }), [searchQuery, currentView, viewConfig]);

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}

/**
 * Hook to consume search context.
 * Returns safe fallback when used outside provider (e.g. modals, standalone components).
 */
export function useSearch() {
  const ctx = useContext(SearchContext);
  if (!ctx) {
    return {
      searchQuery: '',
      setSearchQuery: () => {},
      clearSearch: () => {},
      currentView: null,
      viewConfig: DEFAULT_CONFIG
    };
  }
  return ctx;
}

export { VIEW_SEARCH_CONFIG };
export default SearchContext;
